import { useState } from 'react'
import { authApi } from '../api/authApi'
import { generateCodeVerifier, generateCodeChallenge, PKCE_VERIFIER_KEY } from '../utils/pkce'

interface OAuthLoginProps {
  onError?: (message: string) => void
  returnTo?: string
}

export default function OAuthLogin({ onError, returnTo = '/customer' }: OAuthLoginProps) {
  const [loading, setLoading] = useState<'google' | 'microsoft' | 'apple' | 'facebook' | null>(null)

  const redirectUri = `${window.location.origin}/auth/callback`

  const handleGoogle = async () => {
    setLoading('google')
    try {
      sessionStorage.setItem('oauth_return_to', returnTo)
      const url = await authApi.getGoogleAuthUrl(redirectUri)
      window.location.href = url
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Google sign-in unavailable')
      setLoading(null)
    }
  }

  const handleMicrosoft = async () => {
    setLoading('microsoft')
    try {
      sessionStorage.setItem('oauth_return_to', returnTo)
      const codeVerifier = generateCodeVerifier()
      const codeChallenge = await generateCodeChallenge(codeVerifier)
      sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier)
      const url = await authApi.getMicrosoftAuthUrl(redirectUri, codeChallenge)
      window.location.href = url
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Microsoft sign-in unavailable')
      setLoading(null)
    }
  }

  const handleApple = async () => {
    setLoading('apple')
    try {
      sessionStorage.setItem('oauth_return_to', returnTo)
      const url = await authApi.getAppleAuthUrl(redirectUri)
      window.location.href = url
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Apple sign-in unavailable')
      setLoading(null)
    }
  }

  const handleFacebook = async () => {
    setLoading('facebook')
    try {
      sessionStorage.setItem('oauth_return_to', returnTo)
      const url = await authApi.getFacebookAuthUrl(redirectUri)
      window.location.href = url
    } catch {
      onError?.("We're still convincing Facebook to work with us 🤝 — hang tight while we sort out the paperwork. Try Google or Microsoft for now!")
      setLoading(null)
    }
  }

  return (
    <div className="space-y-3">
      <button
        onClick={handleGoogle}
        disabled={!!loading}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {loading === 'google' ? 'Redirecting...' : 'Sign in with Google'}
      </button>
      <button
        onClick={handleMicrosoft}
        disabled={!!loading}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#f35325" d="M1 1h10v10H1z" />
          <path fill="#81bc06" d="M1 13h10v10H1z" />
          <path fill="#05a6f0" d="M13 1h10v10H13z" />
          <path fill="#ffba08" d="M13 13h10v10H13z" />
        </svg>
        {loading === 'microsoft' ? 'Redirecting...' : 'Sign in with Microsoft'}
      </button>
      <button
        onClick={handleApple}
        disabled={!!loading}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-300 bg-black text-white font-medium hover:bg-gray-900 disabled:opacity-50"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
        </svg>
        {loading === 'apple' ? 'Redirecting...' : 'Sign in with Apple'}
      </button>
      <button
        onClick={handleFacebook}
        disabled={!!loading}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
        {loading === 'facebook' ? 'Redirecting...' : 'Sign in with Facebook'}
      </button>
    </div>
  )
}
